(() => {
  const SELECTOR = '[data-drift-video]';
  const MOBILE_QUERY = '(max-width: 768px)';
  const DESKTOP_TRIGGER_RATIO = 0.70;


  function initVideo(section) {
    if (
      !section ||
      section.dataset.driftVideoInitialized === 'true'
    ) {
      return;
    }


    const video =
      section.querySelector('[data-drift-video-media]');

    const playButton =
      section.querySelector('[data-drift-video-play]');

    const replayButton =
      section.querySelector('[data-drift-video-replay]');

    const soundButton =
      section.querySelector('[data-drift-video-sound]');

    const soundLabel =
      section.querySelector('[data-drift-video-sound-label]');


    if (!video) return;


    section.dataset.driftVideoInitialized = 'true';


    const mobileMedia =
      window.matchMedia(MOBILE_QUERY);


    let desktopStarted = false;
    let desktopUserUnmuted = false;
    let userStartedMobile = false;

    let desktopObserver = null;
    let ticking = false;


    /* ============================================================
       HELPERS
       ============================================================ */

    const getVisibleRatio = () => {
      const rect =
        section.getBoundingClientRect();

      const viewportHeight =
        window.innerHeight ||
        document.documentElement.clientHeight ||
        1;

      const visibleTop =
        Math.max(0, rect.top);

      const visibleBottom =
        Math.min(viewportHeight, rect.bottom);

      const visibleHeight =
        Math.max(
          0,
          visibleBottom - visibleTop
        );

      const referenceHeight =
        Math.max(
          1,
          Math.min(rect.height, viewportHeight)
        );

      return visibleHeight / referenceHeight;
    };


    const setPlayingState = (playing) => {
      section.classList.toggle(
        'is-playing',
        playing
      );


      if (playButton) {
        playButton.setAttribute(
          'aria-label',
          playing ? 'Pause video' : 'Play video'
        );
      }
    };


    const setSoundState = (muted) => {
      section.classList.toggle(
        'is-muted',
        muted
      );

      section.classList.toggle(
        'is-unmuted',
        !muted
      );


      if (soundButton) {
        soundButton.setAttribute(
          'aria-pressed',
          String(!muted)
        );

        soundButton.setAttribute(
          'aria-label',
          muted ? 'Unmute video' : 'Mute video'
        );
      }


      if (soundLabel) {
        soundLabel.textContent =
          muted ? 'UNMUTE' : 'MUTE';
      }
    };


    const ensureLooping = () => {
      video.loop = true;
      video.setAttribute('loop', '');
      video.playsInline = true;
      video.setAttribute('playsinline', '');
    };


    /* ============================================================
       DESKTOP >768
       Start ONCE when 50% visible, then loop continuously.
       ============================================================ */

    const requestDesktopPlay = async () => {
      if (
        mobileMedia.matches ||
        !desktopStarted
      ) {
        return;
      }


      ensureLooping();


      /*
        Initial desktop playback is muted so the browser can start it
        without a user gesture. Preserve a later explicit unmute choice.
      */
      video.muted =
        desktopUserUnmuted ? false : true;

      video.defaultMuted = true;


      setSoundState(
        video.muted || video.volume === 0
      );


      try {
        await video.play();

        setPlayingState(true);
      } catch (_) {
        /*
          If an unmuted resume is blocked, fall back to muted playback.
        */
        try {
          desktopUserUnmuted = false;

          video.muted = true;

          setSoundState(true);

          await video.play();

          setPlayingState(true);
        } catch (_) {
          setPlayingState(false);
        }
      }
    };


    const startDesktopOnce = () => {
      if (
        mobileMedia.matches ||
        desktopStarted
      ) {
        return;
      }


      desktopStarted = true;


      try {
        video.currentTime = 0;
      } catch (_) {}


      requestDesktopPlay();


      /*
        Once the video has started, we no longer need the trigger observer.
        Native `loop` keeps it repeating.
      */
      desktopObserver?.disconnect();
      desktopObserver = null;
    };


    const desktopSafetyCheck = () => {
      ticking = false;


      if (mobileMedia.matches) return;


      const ratio =
        getVisibleRatio();


      if (
        !desktopStarted &&
        ratio >= DESKTOP_TRIGGER_RATIO
      ) {
        startDesktopOnce();

        return;
      }


      /*
        Browser/tab restoration can suspend a looping video.
        Resume only when the section is actually visible again.
      */
      if (
        desktopStarted &&
        ratio > 0 &&
        video.paused
      ) {
        requestDesktopPlay();
      }
    };


    function requestDesktopSafetyCheck() {
      if (ticking) return;

      ticking = true;

      requestAnimationFrame(
        desktopSafetyCheck
      );
    }


    const setupDesktopTrigger = () => {
      if (mobileMedia.matches) return;


      /*
        Do NOT autoplay at page load.
        Leave the poster / first frame visible until the section reaches 50%.
      */
      if (!desktopStarted) {
        try {
          video.pause();
        } catch (_) {}


        if (
          'IntersectionObserver' in window
        ) {
          desktopObserver?.disconnect();

          desktopObserver =
            new IntersectionObserver(
              (entries) => {
                for (const entry of entries) {
                  if (
                    !desktopStarted &&
                    entry.isIntersecting &&
                    entry.intersectionRatio >= DESKTOP_TRIGGER_RATIO
                  ) {
                    startDesktopOnce();
                    break;
                  }


                  if (
                    desktopStarted &&
                    entry.isIntersecting &&
                    video.paused
                  ) {
                    requestDesktopPlay();
                  }
                }
              },
              {
                threshold: [
                  0,
                  0.25,
                  0.5,
                  0.75,
                  1
                ]
              }
            );


          desktopObserver.observe(section);
        }
      }


      requestDesktopSafetyCheck();
      requestAnimationFrame(
        requestDesktopSafetyCheck
      );
    };


    const toggleDesktopSound = async () => {
      if (mobileMedia.matches) return;


      /*
        If the user interacts with the sound control before the section's
        50% trigger, start the video immediately because the click itself
        is a valid user gesture.
      */
      if (!desktopStarted) {
        desktopStarted = true;

        try {
          video.currentTime = 0;
        } catch (_) {}

        desktopObserver?.disconnect();
        desktopObserver = null;
      }


      ensureLooping();


      if (video.muted || video.volume === 0) {
        try {
          video.muted = false;
          video.volume = 1;

          await video.play();

          desktopUserUnmuted = true;

          setSoundState(false);
          setPlayingState(true);
        } catch (_) {
          desktopUserUnmuted = false;

          video.muted = true;

          setSoundState(true);
        }

        return;
      }


      desktopUserUnmuted = false;

      video.muted = true;

      setSoundState(true);
    };


    const replayDesktopVideo = async () => {
      if (mobileMedia.matches) return;


      if (!desktopStarted) {
        desktopStarted = true;

        desktopObserver?.disconnect();
        desktopObserver = null;
      }


      ensureLooping();


      try {
        video.currentTime = 0;
      } catch (_) {}


      try {
        await video.play();

        setPlayingState(true);

        setSoundState(
          video.muted || video.volume === 0
        );
      } catch (_) {
        requestDesktopPlay();
      }
    };


    /* ============================================================
       MOBILE / TABLET <=768
       Manual play only. Once started, loop continuously.
       ============================================================ */

    const prepareMobile = () => {
      desktopObserver?.disconnect();
      desktopObserver = null;


      desktopUserUnmuted = false;


      try {
        video.pause();
      } catch (_) {}


      if (!userStartedMobile) {
        try {
          video.currentTime = 0;
        } catch (_) {}
      }


      ensureLooping();

      setPlayingState(false);
    };


    const toggleMobilePlayback = async () => {
      if (!mobileMedia.matches) return;


      if (!video.paused) {
        video.pause();

        setPlayingState(false);

        return;
      }


      userStartedMobile = true;

      ensureLooping();


      try {
        video.muted = false;
        video.volume = 1;

        await video.play();

        setPlayingState(true);
      } catch (_) {
        /*
          Defensive fallback only.
        */
        try {
          video.muted = true;

          await video.play();

          setPlayingState(true);
        } catch (_) {
          setPlayingState(false);
        }
      }
    };


    /* ============================================================
       RESPONSIVE MODE SWITCH
       ============================================================ */

    const applyMode = () => {
      if (mobileMedia.matches) {
        prepareMobile();
      } else {
        userStartedMobile = false;

        /*
          If the video started previously on desktop, keep/resume it.
          Otherwise wait for the 50% viewport trigger.
        */
        if (desktopStarted) {
          requestDesktopPlay();
        } else {
          setupDesktopTrigger();
        }
      }
    };


    const handleMediaChange = () => {
      applyMode();
    };


    /* ============================================================
       CONTROL / VIDEO EVENTS
       ============================================================ */

    const handlePlayButton = (event) => {
      if (!mobileMedia.matches) return;

      event.preventDefault();

      toggleMobilePlayback();
    };


    const handleReplayButton = (event) => {
      if (mobileMedia.matches) return;

      event.preventDefault();

      replayDesktopVideo();
    };


    const handleSoundButton = (event) => {
      if (mobileMedia.matches) return;

      event.preventDefault();

      toggleDesktopSound();
    };


    const handleVideoTap = () => {
      if (
        mobileMedia.matches &&
        userStartedMobile
      ) {
        toggleMobilePlayback();
      }
    };


    const handlePlaying = () => {
      /*
        Defensive guard: mobile must never autoplay.
      */
      if (
        mobileMedia.matches &&
        !userStartedMobile
      ) {
        prepareMobile();

        return;
      }


      setPlayingState(true);


      if (!mobileMedia.matches) {
        setSoundState(
          video.muted || video.volume === 0
        );
      }
    };


    const handlePause = () => {
      setPlayingState(false);
    };


    const handleVolumeChange = () => {
      if (mobileMedia.matches) return;


      desktopUserUnmuted =
        !video.muted && video.volume > 0;

      setSoundState(
        video.muted || video.volume === 0
      );
    };


    const handleEnded = () => {
      /*
        `loop` should prevent this in normal operation.
        Keep a defensive manual replay fallback.
      */
      if (mobileMedia.matches) {
        if (!userStartedMobile) return;
      } else if (!desktopStarted) {
        return;
      }


      try {
        video.currentTime = 0;
      } catch (_) {}


      if (mobileMedia.matches) {
        video.play().catch(() => {});
      } else {
        requestDesktopPlay();
      }
    };


    const handlePageShow = () => {
      if (mobileMedia.matches) {
        prepareMobile();

        return;
      }


      if (desktopStarted) {
        if (video.paused) {
          requestDesktopPlay();
        }
      } else {
        setupDesktopTrigger();
      }
    };


    const handleVisibilityChange = () => {
      if (document.hidden) return;


      if (mobileMedia.matches) return;


      if (
        desktopStarted &&
        video.paused &&
        getVisibleRatio() > 0
      ) {
        requestDesktopPlay();
      } else if (!desktopStarted) {
        requestDesktopSafetyCheck();
      }
    };


    /* ============================================================
       BIND
       ============================================================ */

    playButton?.addEventListener(
      'click',
      handlePlayButton
    );


    replayButton?.addEventListener(
      'click',
      handleReplayButton
    );


    soundButton?.addEventListener(
      'click',
      handleSoundButton
    );


    video.addEventListener(
      'click',
      handleVideoTap
    );


    video.addEventListener(
      'playing',
      handlePlaying
    );


    video.addEventListener(
      'pause',
      handlePause
    );


    video.addEventListener(
      'volumechange',
      handleVolumeChange
    );


    video.addEventListener(
      'ended',
      handleEnded
    );


    window.addEventListener(
      'scroll',
      requestDesktopSafetyCheck,
      { passive: true }
    );


    window.addEventListener(
      'resize',
      requestDesktopSafetyCheck
    );


    window.addEventListener(
      'pageshow',
      handlePageShow
    );


    document.addEventListener(
      'visibilitychange',
      handleVisibilityChange
    );


    if (
      typeof mobileMedia.addEventListener === 'function'
    ) {
      mobileMedia.addEventListener(
        'change',
        handleMediaChange
      );
    } else {
      mobileMedia.addListener(
        handleMediaChange
      );
    }


    /* ============================================================
       CLEANUP
       ============================================================ */

    section.__driftVideoCleanup = () => {
      desktopObserver?.disconnect();


      playButton?.removeEventListener(
        'click',
        handlePlayButton
      );


      replayButton?.removeEventListener(
        'click',
        handleReplayButton
      );


      soundButton?.removeEventListener(
        'click',
        handleSoundButton
      );


      video.removeEventListener(
        'click',
        handleVideoTap
      );


      video.removeEventListener(
        'playing',
        handlePlaying
      );


      video.removeEventListener(
        'pause',
        handlePause
      );


      video.removeEventListener(
        'volumechange',
        handleVolumeChange
      );


      video.removeEventListener(
        'ended',
        handleEnded
      );


      window.removeEventListener(
        'scroll',
        requestDesktopSafetyCheck
      );


      window.removeEventListener(
        'resize',
        requestDesktopSafetyCheck
      );


      window.removeEventListener(
        'pageshow',
        handlePageShow
      );


      document.removeEventListener(
        'visibilitychange',
        handleVisibilityChange
      );


      if (
        typeof mobileMedia.removeEventListener === 'function'
      ) {
        mobileMedia.removeEventListener(
          'change',
          handleMediaChange
        );
      } else {
        mobileMedia.removeListener(
          handleMediaChange
        );
      }


      delete section.dataset.driftVideoInitialized;
    };


    ensureLooping();
    setSoundState(true);
    applyMode();
  }


  function initAll(root = document) {
    if (root.matches?.(SELECTOR)) {
      initVideo(root);
    }


    root.querySelectorAll?.(SELECTOR).forEach(
      initVideo
    );
  }


  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => initAll(),
      { once: true }
    );
  } else {
    initAll();
  }


  document.addEventListener(
    'shopify:section:load',
    (event) => initAll(event.target)
  );


  document.addEventListener(
    'shopify:section:select',
    (event) => initAll(event.target)
  );


  document.addEventListener(
    'shopify:section:unload',
    (event) => {
      const section =
        event.target.matches?.(SELECTOR)
          ? event.target
          : event.target.querySelector?.(SELECTOR);


      if (section?.__driftVideoCleanup) {
        section.__driftVideoCleanup();
      }
    }
  );
})();
